// PRD §15.5 — historic per-recording transcript embed.
//
// Route: /embed/conversation/:conversationId/transcript
//
// Renders paginated CLEANED_TRANSCRIPT detail items for the conversation.
// "Speaker labels hidden until tagging arrives" is irrelevant here — this
// is the historic view, by definition all batches are already cleaned.

import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Center,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import EmbedLayout from "./EmbedLayout";
import { getCleanedTranscriptItems } from "../../services/embed";
import {
  CleanedTurn,
  SourceMarker,
  flattenCleanedTurns,
} from "../CleanedTranscriptView";

function shouldIncludeTurn(turn, speakerFilter) {
  if (speakerFilter !== "employee") return true;
  return String(turn?.role || turn?.speaker || "")
    .trim()
    .toLowerCase()
    .startsWith("employee");
}

function TranscriptTurn({ turn, meta, sourceMarker, sourceIndex, index }) {
  return (
    <Box
      data-testid="transcript-bubble"
      data-speaker={turn?.role || turn?.speaker || "unknown"}
    >
      {sourceMarker && (
        <SourceMarker sourceId={sourceMarker} sourceIndex={sourceIndex} />
      )}
      <CleanedTurn turn={turn} meta={meta} turnIdx={index} isActive={false} />
    </Box>
  );
}

export default function EmbedTranscriptView() {
  const { conversationId } = useParams();
  const [searchParams] = useSearchParams();
  const speakerFilter = (searchParams.get("speaker") || "employee")
    .trim()
    .toLowerCase();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const {
    allTurns,
    allMeta,
    allSourceIds,
  } = flattenCleanedTurns(items);

  const visibleRows = allTurns.reduce((rows, turn, idx) => {
    if (!shouldIncludeTurn(turn, speakerFilter)) return rows;
    rows.push({
      turn,
      meta: allMeta[idx],
      sourceId: allSourceIds[idx],
      originalIndex: idx,
    });
    return rows;
  }, []);

  const sourceOrdinalById = new Map();
  visibleRows.forEach((row) => {
    if (row.sourceId == null || sourceOrdinalById.has(row.sourceId)) return;
    sourceOrdinalById.set(row.sourceId, sourceOrdinalById.size);
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    getCleanedTranscriptItems(conversationId, { skip: 0, limit: 200 })
      .then((data) => {
        if (cancelled) return;
        const list =
          data?.items || (Array.isArray(data) ? data : []) || [];
        setItems(list);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message || "Failed to load transcript.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [conversationId]);

  return (
    <EmbedLayout>
      <Box w="100%" px={4} py={3} data-testid="embed-transcript-view">
        {loading && (
          <Center py={8}>
            <Spinner size="md" />
          </Center>
        )}

        {!loading && error && (
          <Text color="red.500" data-testid="embed-transcript-error">
            {error}
          </Text>
        )}

        {!loading && !error && visibleRows.length === 0 && (
          <Text color="gray.500" data-testid="embed-transcript-empty">
            {speakerFilter === "employee"
              ? "No employee transcript yet."
              : "No transcript yet."}
          </Text>
        )}

        {!loading && !error && visibleRows.length > 0 && (
          <VStack align="stretch" spacing={3}>
            {visibleRows.map((row, idx) => {
              const prev = idx > 0 ? visibleRows[idx - 1] : null;
              const showSourceMarker =
                row.sourceId != null && row.sourceId !== prev?.sourceId;
              return (
                <TranscriptTurn
                  key={`${row.sourceId ?? "turn"}-${row.originalIndex}`}
                  turn={row.turn}
                  meta={row.meta}
                  sourceMarker={showSourceMarker ? row.sourceId : null}
                  sourceIndex={sourceOrdinalById.get(row.sourceId) ?? 0}
                  index={row.originalIndex}
                />
              );
            })}
          </VStack>
        )}
      </Box>
    </EmbedLayout>
  );
}
